import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { toSlackText, unescapeHtml, type SlackUser } from '@assembly/slack'
import { searchEmoji } from '../emoji'
import { useActions } from '../data/actions'
import { useConversation, useConversationName, useConversations, useCustomEmoji, useUsers, displayName, fullName } from '../data/hooks'
import { useUi } from '../state/ui'
import { EmojiPicker } from './EmojiPicker'
import { Icon } from './Icon'
import { Avatar, Emoji, IconButton, Menu, Modal, anchorFrom, useIsNarrow, type Anchor } from './common'

type Known = { text: string; replacement: string }

interface Suggestion {
  key: string
  insert: string
  known?: Known
  label: React.ReactNode
}

/** Converts Slack wire text back into editable text, remembering entity mappings. */
function useEditableText() {
  const { data: users } = useUsers()
  const { data: convs } = useConversations()
  return useCallback(
    (text: string): { text: string; known: Known[] } => {
      const known: Known[] = []
      const out = text.replace(/<([^>\n]+)>/g, (whole, body: string) => {
        const [target, label] = body.split('|')
        if (target.startsWith('@')) {
          const t = `@${displayName(users?.get(target.slice(1)), label ?? target.slice(1))}`
          known.push({ text: t, replacement: `<${target}>` })
          return t
        }
        if (target.startsWith('#')) {
          const name = label ?? convs?.find((c) => c.id === target.slice(1))?.name ?? target.slice(1)
          known.push({ text: `#${name}`, replacement: `<${target}|${name}>` })
          return `#${name}`
        }
        if (target.startsWith('!')) return `@${target.slice(1).split('^')[0]}`
        if (label && label !== target) {
          known.push({ text: unescapeHtml(label), replacement: whole })
          return label
        }
        return target
      })
      return { text: unescapeHtml(out), known }
    },
    [users, convs],
  )
}

const SLASH_HELP = [
  ['/me', 'text', 'Display italicized action text'],
  ['/shrug', 'text', 'Append ¯\\_(ツ)_/¯ to your message'],
  ['/topic', 'text', 'Set the channel topic'],
  ['/purpose', 'text', 'Set the channel description'],
  ['/rename', 'name', 'Rename the channel'],
  ['/invite', '@person', 'Add people to the channel'],
  ['/leave', '', 'Leave the channel'],
  ['/archive', '', 'Archive the channel'],
  ['/remind', 'me to … in 10 minutes', 'Set a reminder'],
  ['/status', ':emoji: text', 'Set your status (empty to clear)'],
  ['/away', '', 'Set yourself away'],
  ['/active', '', 'Set yourself active'],
  ['/dnd', 'minutes', 'Pause notifications'],
  ['/search', 'query', 'Search messages'],
] as const

export function Composer({
  channel,
  threadTs,
  editing,
  onDone,
  onEditLast,
}: {
  channel: string
  threadTs?: string
  editing?: { ts: string; text: string }
  onDone?: () => void
  onEditLast?: () => void
}) {
  const actions = useActions()
  const narrow = useIsNarrow()
  const { data: users } = useUsers()
  const { data: convs } = useConversations()
  const { data: conv } = useConversation(channel)
  const convName = useConversationName()
  const { data: customEmoji } = useCustomEmoji()
  const toEditable = useEditableText()
  const draftKey = threadTs ? `${channel}:${threadTs}` : channel
  const draft = useUi((s) => s.drafts[draftKey])
  const setDraft = useUi((s) => s.setDraft)
  const ui = useUi.getState

  const initial = useMemo(() => (editing ? toEditable(editing.text) : { text: draft ?? '', known: [] as Known[] }), []) // eslint-disable-line react-hooks/exhaustive-deps
  const [text, setText] = useState(initial.text)
  const known = useRef<Known[]>(initial.known)
  const [files, setFiles] = useState<File[]>([])
  const [broadcast, setBroadcast] = useState(false)
  const [caret, setCaret] = useState(0)
  const [selected, setSelected] = useState(0)
  const [dismissed, setDismissed] = useState<string | null>(null)
  const [emojiAnchor, setEmojiAnchor] = useState<Anchor | null>(null)
  const [scheduleAnchor, setScheduleAnchor] = useState<Anchor | null>(null)
  const [customSchedule, setCustomSchedule] = useState(false)
  const [sending, setSending] = useState(false)
  const [dragging, setDragging] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  // Persist drafts (not while editing an existing message).
  useEffect(() => {
    if (!editing) setDraft(draftKey, text)
  }, [text, draftKey, editing, setDraft])

  // Auto-size.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, narrow ? 140 : 280)}px`
  }, [text, narrow])

  useEffect(() => {
    // Don't pull focus out of an open dialog (e.g. the quick switcher) when a channel finishes loading.
    if ((editing || !narrow) && !useUi.getState().modal) ref.current?.focus({ preventScroll: true })
    if (editing) ref.current?.setSelectionRange(ref.current.value.length, ref.current.value.length)
  }, [channel, threadTs, editing, narrow])

  // ---------------------------------------------------------- autocomplete
  const trigger = useMemo(() => {
    const before = text.slice(0, caret)
    const m = /(^|\s)([@#:\/])([\w.\-+']*)$/.exec(before)
    if (!m) return null
    if (m[2] === '/' && (m[1] !== '' || before.length !== m[0].length)) return null // slash only at start
    if (m[2] === ':' && m[3].length < 2) return null
    const t = { char: m[2], query: m[3].toLowerCase(), start: caret - m[3].length - 1 }
    return dismissed === `${t.start}${t.char}` ? null : t
  }, [text, caret, dismissed])

  const suggestions = useMemo((): Suggestion[] => {
    if (!trigger) return []
    const q = trigger.query
    if (trigger.char === '@') {
      const list: Suggestion[] = []
      const people = [...(users?.values() ?? [])]
        .filter((u) => !u.deleted && !u.is_bot && u.id !== 'USLACKBOT')
        .filter((u) => !q || u.name.toLowerCase().includes(q) || displayName(u).toLowerCase().includes(q) || fullName(u).toLowerCase().includes(q))
        .sort((a, b) => Number(displayName(b).toLowerCase().startsWith(q)) - Number(displayName(a).toLowerCase().startsWith(q)))
        .slice(0, 8)
      for (const u of people) {
        const insert = `@${displayName(u)}`
        list.push({
          key: u.id,
          insert,
          known: { text: insert, replacement: `<@${u.id}>` },
          label: <SuggestUser user={u} />,
        })
      }
      for (const special of ['here', 'channel', 'everyone']) {
        if (special.startsWith(q) && !conv?.is_im) list.push({ key: special, insert: `@${special}`, label: <span className="suggest-row"><strong>@{special}</strong><span className="muted">Notify {special === 'here' ? 'active members' : 'everyone in this channel'}</span></span> })
      }
      return list
    }
    if (trigger.char === '#') {
      return (convs ?? [])
        .filter((c) => (c.is_channel || c.is_group) && !c.is_mpim && (c.name ?? '').includes(q))
        .slice(0, 8)
        .map((c) => ({
          key: c.id,
          insert: `#${c.name}`,
          known: { text: `#${c.name}`, replacement: `<#${c.id}|${c.name}>` },
          label: (
            <span className="suggest-row">
              <Icon name={c.is_private ? 'lock' : 'hash'} size={14} />
              <strong>{convName(c)}</strong>
            </span>
          ),
        }))
    }
    if (trigger.char === ':') {
      return searchEmoji(q, customEmoji, 8).map((name) => ({
        key: name,
        insert: `:${name}:`,
        label: (
          <span className="suggest-row">
            <Emoji name={name} />
            <span>:{name}:</span>
          </span>
        ),
      }))
    }
    if (trigger.char === '/' && !editing) {
      return SLASH_HELP.filter(([cmd]) => cmd.slice(1).startsWith(q)).map(([cmd, args, help]) => ({
        key: cmd,
        insert: cmd,
        label: (
          <span className="suggest-row">
            <strong>{cmd}</strong>
            <span className="muted">{args}</span>
            <span className="muted grow-left">{help}</span>
          </span>
        ),
      }))
    }
    return []
  }, [trigger, users, convs, conv, customEmoji, convName, editing])

  useEffect(() => setSelected(0), [trigger?.char, trigger?.query])

  const applySuggestion = (s: Suggestion) => {
    if (!trigger) return
    const before = text.slice(0, trigger.start)
    const after = text.slice(caret)
    const next = `${before}${s.insert} ${after.replace(/^\s/, '')}`
    if (s.known) known.current.push(s.known)
    setText(next)
    const pos = before.length + s.insert.length + 1
    requestAnimationFrame(() => {
      ref.current?.setSelectionRange(pos, pos)
      setCaret(pos)
    })
  }

  // ---------------------------------------------------------- sending
  const encode = (input: string) =>
    toSlackText(
      input,
      {
        user: (handle) => {
          const h = handle.toLowerCase()
          for (const u of users?.values() ?? []) if (u.name.toLowerCase() === h || displayName(u).toLowerCase().replace(/\s/g, '') === h) return u.id
          return undefined
        },
        channel: (name) => convs?.find((c) => c.name === name)?.id,
      },
      known.current,
    )

  const reset = () => {
    setText('')
    setFiles([])
    known.current = []
    setDraft(draftKey, '')
  }

  /** Handles /commands. Returns true when the input was consumed. */
  const runSlash = async (input: string): Promise<boolean> => {
    const m = /^\/(\w+)\s*([\s\S]*)$/.exec(input)
    if (!m) return false
    const [, cmd, arg] = m
    switch (cmd) {
      case 'me':
        await actions.meMessage(channel, encode(arg))
        return true
      case 'shrug':
        await actions.sendMessage(channel, encode(`${arg} ¯\\_(ツ)_/¯`.trim()), { thread_ts: threadTs })
        return true
      case 'topic':
        await actions.setTopic(channel, arg)
        return true
      case 'purpose':
        await actions.setPurpose(channel, arg)
        return true
      case 'rename':
        await actions.renameChannel(channel, arg.replace(/^#/, '').trim().toLowerCase().replace(/\s+/g, '-'))
        return true
      case 'invite': {
        const ids = known.current.filter((k) => k.replacement.startsWith('<@') && arg.includes(k.text)).map((k) => k.replacement.slice(2, -1))
        if (ids.length) await actions.invite(channel, ids)
        else ui().openModal({ type: 'invite', channel })
        return true
      }
      case 'leave':
        await actions.leaveChannel(channel)
        return true
      case 'archive':
        ui().openModal({ type: 'confirm', title: 'Archive channel?', body: 'Members won’t be able to send messages. You can unarchive it later.', confirm: 'Archive', danger: true, onConfirm: () => actions.archiveChannel(channel) })
        return true
      case 'remind': {
        const r = /^(?:me\s+)?(?:to\s+)?(.+?)\s+((?:in|at|on|every)\s.+|tomorrow.*|today.*|tonight.*)$/i.exec(arg)
        if (!r) {
          ui().showToast('Try “/remind me to stretch in 30 minutes”', 'error')
          return true
        }
        await actions.addReminder(r[1], r[2])
        return true
      }
      case 'status': {
        const s = /^(:[\w+\-]+:)?\s*(.*)$/.exec(arg.trim())!
        await actions.setStatus(s[2] ?? '', s[1] ?? (s[2] ? ':speech_balloon:' : ''))
        return true
      }
      case 'away':
        await actions.setPresence('away')
        return true
      case 'active':
        await actions.setPresence('auto')
        return true
      case 'dnd':
        await actions.snooze(Number(arg) || 60)
        return true
      case 'search':
        ui().setView({ type: 'search', query: arg })
        return true
      default:
        ui().showToast(`/${cmd} isn’t supported in Assembly`, 'error')
        return true
    }
  }

  const submit = async (scheduleAt?: Date) => {
    const input = text.trim()
    if (editing && !input) {
      // Clearing a message and pressing Enter deletes it (after confirming), like Slack.
      onDone?.()
      const ts = editing.ts
      ui().openModal({
        type: 'confirm',
        title: 'Delete message',
        body: 'Are you sure you want to delete this message? This can’t be undone.',
        confirm: 'Delete',
        danger: true,
        onConfirm: () => actions.deleteMessage(channel, ts),
      })
      return
    }
    if (sending || (!input && !files.length)) return
    if (editing) {
      onDone?.()
      if (input !== toEditable(editing.text).text) await actions.editMessage(channel, editing.ts, encode(input))
      return
    }
    setSending(true)
    try {
      if (!files.length && !scheduleAt && input.startsWith('/') && (await runSlash(input))) {
        reset()
        return
      }
      const wire = encode(input)
      const toUpload = files
      reset()
      if (scheduleAt) await actions.scheduleMessage(channel, wire, scheduleAt, threadTs)
      else if (toUpload.length) await actions.uploadFiles(channel, toUpload, wire, threadTs)
      else await actions.sendMessage(channel, wire, { thread_ts: threadTs, broadcast: threadTs ? broadcast : undefined })
    } catch {
      // Restore so nothing typed is lost.
      setText(input)
    } finally {
      setSending(false)
      ref.current?.focus()
    }
  }

  const wrapSelection = (left: string, right = left) => {
    const el = ref.current
    if (!el) return
    const { selectionStart: s, selectionEnd: e } = el
    const next = text.slice(0, s) + left + text.slice(s, e) + right + text.slice(e)
    setText(next)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(s + left.length, e + left.length)
    })
  }

  const insertAtCaret = (insert: string) => {
    const el = ref.current
    const pos = el?.selectionStart ?? text.length
    const next = text.slice(0, pos) + insert + text.slice(pos)
    setText(next)
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(pos + insert.length, pos + insert.length)
    })
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelected((i) => (i + 1) % suggestions.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelected((i) => (i - 1 + suggestions.length) % suggestions.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        applySuggestion(suggestions[selected])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setDismissed(`${trigger!.start}${trigger!.char}`)
        return
      }
    }
    const mod = e.metaKey || e.ctrlKey
    if (mod && e.key.toLowerCase() === 'b') return e.preventDefault(), wrapSelection('*')
    if (mod && e.key.toLowerCase() === 'i') return e.preventDefault(), wrapSelection('_')
    if (mod && e.shiftKey && e.key.toLowerCase() === 'x') return e.preventDefault(), wrapSelection('~')
    if (mod && e.shiftKey && e.key.toLowerCase() === 'c') return e.preventDefault(), wrapSelection('`')
    if (e.key === 'Escape' && editing) return e.preventDefault(), onDone?.()
    if (e.key === 'ArrowUp' && !text && !editing && onEditLast) return e.preventDefault(), onEditLast()
    // Enter sends on hardware keyboards; on phones the return key inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.nativeEvent.isComposing && !narrow) {
      e.preventDefault()
      submit()
    }
  }

  const addFiles = (list: FileList | File[] | null) => {
    if (!list) return
    const arr = Array.from(list)
    if (arr.length) setFiles((f) => [...f, ...arr].slice(0, 10))
  }

  const placeholder = editing
    ? 'Edit message'
    : threadTs
      ? 'Reply…'
      : conv
        ? conv.is_im || conv.is_mpim
          ? `Message ${convName(conv)}`
          : `Message #${conv.name}`
        : 'Message'

  const scheduleOptions = () => {
    const now = new Date()
    const in1h = new Date(now.getTime() + 60 * 60_000)
    const tomorrow9 = new Date(now)
    tomorrow9.setDate(now.getDate() + 1)
    tomorrow9.setHours(9, 0, 0, 0)
    const monday9 = new Date(now)
    monday9.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7))
    monday9.setHours(9, 0, 0, 0)
    const fmt = (d: Date) => d.toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })
    return [
      { label: `In 1 hour`, hint: fmt(in1h), icon: 'clock' as const, onSelect: () => submit(in1h) },
      { label: 'Tomorrow morning', hint: fmt(tomorrow9), icon: 'calendar' as const, onSelect: () => submit(tomorrow9) },
      { label: 'Next Monday', hint: fmt(monday9), icon: 'calendar' as const, onSelect: () => submit(monday9) },
      { label: 'Custom time…', icon: 'pencil' as const, onSelect: () => setCustomSchedule(true) },
      'separator' as const,
      { label: 'View scheduled messages', icon: 'clock' as const, onSelect: () => ui().openModal({ type: 'scheduled' }) },
    ]
  }

  const canSend = !!text.trim() || files.length > 0
  const archived = conv?.is_archived

  if (archived && !editing) {
    return (
      <div className="composer composer-disabled">
        <span>This channel is archived.</span>
        <button type="button" className="button" onClick={() => actions.unarchiveChannel(channel)}>
          Unarchive
        </button>
      </div>
    )
  }

  if (conv && !conv.is_member && !conv.is_im && !conv.is_mpim && !editing) {
    return (
      <div className="composer composer-disabled">
        <span>
          You’re viewing <strong>#{conv.name}</strong>
        </span>
        <button type="button" className="button primary" onClick={() => actions.joinChannel(channel)}>
          Join channel
        </button>
      </div>
    )
  }

  return (
    <div
      className={`composer ${editing ? 'composer-editing' : ''} ${dragging ? 'dragging' : ''}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault()
          setDragging(true)
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        addFiles(e.dataTransfer.files)
      }}
    >
      {suggestions.length > 0 && (
        <div className="suggestions" role="listbox">
          {suggestions.map((s, i) => (
            <button
              key={s.key}
              type="button"
              role="option"
              aria-selected={i === selected}
              className={i === selected ? 'selected' : ''}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setSelected(i)}
              onClick={() => applySuggestion(s)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="composer-files">
          {files.map((f, i) => (
            <span key={i} className="file-chip">
              <Icon name={f.type.startsWith('image/') ? 'image' : 'file'} size={14} />
              <span>{f.name}</span>
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((list) => list.filter((_, j) => j !== i))}>
                <Icon name="x" size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <textarea
        ref={ref}
        rows={1}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value)
          setCaret(e.target.selectionStart)
          setDismissed(null)
        }}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
        onKeyDown={onKeyDown}
        onPaste={(e) => {
          if (e.clipboardData.files.length) {
            e.preventDefault()
            addFiles(e.clipboardData.files)
          }
        }}
        aria-label={placeholder}
      />

      <div className="composer-toolbar">
        {!editing && (
          <>
            <IconButton icon="paperclip" label="Attach files" onClick={() => fileInput.current?.click()} />
            <input ref={fileInput} type="file" multiple hidden onChange={(e) => (addFiles(e.target.files), (e.target.value = ''))} />
          </>
        )}
        <IconButton icon="smile" label="Emoji" onClick={(e) => setEmojiAnchor(anchorFrom(e))} />
        <IconButton icon="at" label="Mention someone" onClick={() => insertAtCaret(text && !/\s$/.test(text) ? ' @' : '@')} />
        {!narrow && !threadTs && (
          <>
            <span className="toolbar-sep" />
            <IconButton icon="bold" label="Bold (⌘B)" onClick={() => wrapSelection('*')} />
            <IconButton icon="italic" label="Italic (⌘I)" onClick={() => wrapSelection('_')} />
            <IconButton icon="strike" label="Strikethrough (⌘⇧X)" onClick={() => wrapSelection('~')} />
            <IconButton icon="code" label="Code (⌘⇧C)" onClick={() => wrapSelection('`')} />
          </>
        )}
        {threadTs && !editing && conv && !conv.is_im && (
          <label className="broadcast-toggle">
            <input type="checkbox" checked={broadcast} onChange={(e) => setBroadcast(e.target.checked)} />
            <span>Also send to #{conv.name}</span>
          </label>
        )}
        <span className="toolbar-spacer" />
        {editing ? (
          <>
            <button type="button" className="button" onClick={onDone}>
              Cancel
            </button>
            <button type="button" className="button primary" onClick={() => submit()} disabled={!text.trim()}>
              Save
            </button>
          </>
        ) : (
          <span className="send-group">
            <button type="button" className="send-button" onClick={() => submit()} disabled={!canSend || sending} aria-label="Send">
              <Icon name="send" size={16} />
            </button>
            <button
              type="button"
              className="send-button schedule"
              disabled={!text.trim() || files.length > 0}
              aria-label="Schedule message"
              title="Schedule for later"
              onClick={(e) => setScheduleAnchor(anchorFrom(e))}
            >
              <Icon name="chevronDown" size={14} />
            </button>
          </span>
        )}
      </div>

      {emojiAnchor && <EmojiPicker anchor={emojiAnchor} onClose={() => setEmojiAnchor(null)} onSelect={(name) => insertAtCaret(`:${name}: `)} />}
      {scheduleAnchor && <Menu anchor={scheduleAnchor} placement="above" onClose={() => setScheduleAnchor(null)} items={scheduleOptions()} />}
      {customSchedule && (
        <CustomSchedule
          onCancel={() => setCustomSchedule(false)}
          onPick={(d) => {
            setCustomSchedule(false)
            submit(d)
          }}
        />
      )}
    </div>
  )
}

function SuggestUser({ user }: { user: SlackUser }) {
  return (
    <span className="suggest-row">
      <Avatar user={user} size={20} />
      <strong>{displayName(user)}</strong>
      {fullName(user) !== displayName(user) && <span className="muted">{fullName(user)}</span>}
    </span>
  )
}

function CustomSchedule({ onPick, onCancel }: { onPick: (d: Date) => void; onCancel: () => void }) {
  const pad = (n: number) => String(n).padStart(2, '0')
  const d = new Date(Date.now() + 60 * 60_000)
  const [value, setValue] = useState(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`)
  const date = new Date(value)
  const valid = !isNaN(date.getTime()) && date.getTime() > Date.now() + 60_000
  return (
    <Modal
      title="Schedule message"
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="button" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="button primary" disabled={!valid} onClick={() => onPick(date)}>
            Schedule
          </button>
        </>
      }
    >
      <label className="field">
        <span>Send at</span>
        <input type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} />
      </label>
      {!valid && <p className="field-error">Pick a time in the future.</p>}
    </Modal>
  )
}
